import React, { useState } from 'react';

import Tooltip from '@material-ui/core/Tooltip';
import styles from './LogRow.module.css';

import Common from '@silverfort-nodelibs/common/utils/common';
import { AD_ENTITY_TYPES, DIRECTORY_TYPES, USER_TYPES, PROTOCOL_DISPLAY_NAMES } from '@silverfort-nodelibs/common/utils/consts';
import LogRowExpender from './LogRowExpender';
import machineAccountIcon from './Images/machine_account.png';
import collapseIcon from '../common/Images/dropdown_expanded.png';
import expandIcon from '../common/Images/dropdown_collapsed.png';
import investigateIcon from '../common/Images/investigation_blue.png';
import InvestigationDrawer from '../InvestigationDrawer/InvestigationDrawer';
import { FeatureLock } from '../common/LockedFeatures';
import { LOGS_COLUMNS_BY_PRICING_MODULES } from './consts';
import { usePricingModulesState } from '../../context/contexts/PricingModuleContext';
import { DisabledDisplayOptions } from '../SystemSettings/hooks/lockedFeatures/types';

const LogIcon = ({ logData, appDrawerContext }) => {
    if (logData.user_type === USER_TYPES.MACHINE_ACCOUNT) {
        return (
            <div className={styles.investigateWarper}>
                <img alt="Machine account icon" title={'Machine Account'} src={machineAccountIcon} />
            </div>
        );
    }
    if (logData.user_guid || logData.external_user_id) {
        return (
            <div className={`${styles.investigateWarper}`}>
                <div
                    className={`${styles.investigate}`}
                    title={'Investigate'}
                    onClick={() =>
                        appDrawerContext.displayContent(
                            <InvestigationDrawer
                                guid={logData.user_guid || logData.external_user_id}
                                identifier={logData.user_identifier}
                                adType={AD_ENTITY_TYPES.USER}
                            />
                        )
                    }>
                    <img alt="investigate" src={investigateIcon} height={14} />
                </div>
            </div>
        );
    }
    return <div />;
};

const LogRow = ({
    appDrawerContext,
    logData,
    rulesNames,
    wallNames,
    user,
    allowDesktopMfa,
    displayAccountType,
    countriesList,
    hiddenRiskIndicators,
    defaultDestinations,
    internalServiceAccounts,
    ssoVendors,
    groupPolicies
}) => {
    const [isExpended, setIsExpended] = useState(false);

    const { pricingModulesState } = usePricingModulesState();
    const toggleExpender = () => {
        setIsExpended(!isExpended);
    };

    const getEmptyStringIfNA = (value) => {
        if (!value || value.toLowerCase() === 'n/a') {
            return '';
        }
        return value;
    };

    const username = getEmptyStringIfNA(Common.formatUsername(logData, displayAccountType));

    const [ruleClass, ruleAction] = Common.formatRuleAction(
        logData.effective_rule_action,
        logData.effective_rule_id,
        false,
        logData.protocol_type
    );
    const [userResponse, userResponseTooltipText] = Common.formatUserResponse(
        logData.user_response,
        logData.effective_rule_action
    );
    const [resultClass, result, reasonTooltipText] = Common.formatResult(
        logData.action,
        logData.reason,
        false,
        logData.error_code
    );
    const userDomain = getEmptyStringIfNA(
        logData.user_domain ? logData.user_domain.toLowerCase() : logData.user_domain
    );
    const serverDomain = getEmptyStringIfNA(logData.server_domain);

    const timestamp = Common.formatDate(logData.time_stamp, navigator).split(' ');
    const service = Common.formatService(logData.protocol_type, logData.service, logData.krb_renewal);

    const [logDestination, isDefaultDestination] = Common.formatDestination(
        logData.server,
        logData.server_ip,
        logData.protocol_type,
        logData.directory_type,
        defaultDestinations
    );

    const directoryType =
        logData.directory_type && DIRECTORY_TYPES[logData.directory_type]
            ? DIRECTORY_TYPES[logData.directory_type].key
            : DIRECTORY_TYPES.ad.key;

    const isServerIpEnabled = logData.server_ip && logData.protocol_type.toLowerCase() === 'ntlm';

    const displayUserResponse = userResponse && (userResponse !== 'n/a' || ruleAction === 'MFA');

    const hostname = getEmptyStringIfNA(logData.host_name);
    const ipAddress = getEmptyStringIfNA(logData.ip_address);

    const displayRuleAction = pricingModulesState
        ? LOGS_COLUMNS_BY_PRICING_MODULES.ruleAction(pricingModulesState)
        : true;
    const displayRisk = pricingModulesState ? LOGS_COLUMNS_BY_PRICING_MODULES.risk(pricingModulesState) : true;

    return (
        <div className={isExpended ? styles.expended : ''}>
            <div className={styles.logRow} onClick={toggleExpender}>
                <div>
                    <div className={styles.topRow}>{timestamp[1]}</div>
                    <div className={styles.bottomRow}>{timestamp[0]}</div>
                </div>
                {!logData.user_identifier &&
                    (logData.protocol_type === PROTOCOL_DISPLAY_NAMES.LDAP ||
                        logData.protocol_type === PROTOCOL_DISPLAY_NAMES.LDAPS) ? (
                    <div>Anonymous LDAP Bind</div>
                ) : (
                    <div>
                        <div className={`${styles.topRow} ${styles.entityName}`} title={username}>
                            {username}
                            <LogIcon appDrawerContext={appDrawerContext} logData={logData} />
                        </div>
                        <div className={styles.bottomRow} title={userDomain}>
                            {userDomain}
                        </div>
                    </div>
                )}
                <div>
                    <div className={styles.topRow} title={hostname}>
                        {hostname}
                        {logData.host_guid && (
                            <div className={`${styles.investigateWarper}`}>
                                <div
                                    className={`${styles.investigate}`}
                                    title={'Investigate'}
                                    onClick={() =>
                                        appDrawerContext.displayContent(
                                            <InvestigationDrawer
                                                guid={logData.host_guid}
                                                identifier={hostname}
                                                adType={AD_ENTITY_TYPES.SERVER}
                                            />
                                        )
                                    }>
                                    <img alt="investigate" src={investigateIcon} />
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={styles.bottomRow} title={ipAddress}>
                        {ipAddress}
                    </div>
                </div>
                <div>
                    <div
                        className={styles.topRow}
                        title={`${logDestination}${serverDomain ? `(${serverDomain})` : ''}`}>
                        {logDestination}
                        {/* we don't support any longer octa or application for investigation */}
                        {!logData.is_application && !isDefaultDestination && logData.server_guid && (
                            <div className={`${styles.investigateWarper}`}>
                                <div
                                    className={`${styles.investigate}`}
                                    title={'Investigate'}
                                    onClick={() =>
                                        appDrawerContext.displayContent(
                                            <InvestigationDrawer
                                                guid={logData.server_guid}
                                                identifier={logData.server}
                                                adType={AD_ENTITY_TYPES.SERVER}
                                            />
                                        )
                                    }>
                                    <img alt="investigate" src={investigateIcon} />
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={styles.bottomRow} title={isServerIpEnabled ? logData.server_ip : service}>
                        {isServerIpEnabled ? logData.server_ip : service}
                    </div>
                </div>
                <div>
                    <FeatureLock
                        displayOptions={{
                            enabled: displayRisk,
                            disabledDisplay: displayRisk ? undefined : DisabledDisplayOptions.HIDDEN
                        }}>
                        <div>{Common.riskLevelToText(logData.risk_level)}</div>
                    </FeatureLock>
                </div>
                <div>
                    <div className={styles.topRow} title={DIRECTORY_TYPES[directoryType].displayName}>
                        {DIRECTORY_TYPES[directoryType].displayName}
                    </div>
                    <div className={styles.bottomRow} title={logData.protocol_type}>
                        {logData.protocol_type}
                    </div>
                </div>
                <div>
                    <FeatureLock
                        displayOptions={{
                            enabled: displayRuleAction,
                            disabledDisplay: displayRuleAction ? undefined : DisabledDisplayOptions.HIDDEN
                        }}>
                        <div className={`${styles.topRow} ${ruleClass}`} title={hostname}>
                            {ruleAction}
                        </div>
                        {displayUserResponse && (
                            <>
                                <span className={`${styles.bottomRow} flex items-center`} title={logData.ip_address}>
                                    {userResponse}
                                    {userResponseTooltipText && (
                                        <Tooltip
                                            title={<div>{userResponseTooltipText}</div>}
                                            enterDelay={100}
                                            placement={'bottom'}
                                            classes={{ tooltip: 'moreInfoIcon-tooltip' }}>
                                            <div className="moreInfoIcon"></div>
                                        </Tooltip>
                                    )}
                                </span>
                            </>
                        )}
                    </FeatureLock>
                </div>
                <div className={styles.result}>
                    <span className={resultClass}>{result}</span>
                    {reasonTooltipText && (
                        <Tooltip
                            title={<div>{reasonTooltipText}</div>}
                            enterDelay={100}
                            placement={'bottom'}
                            classes={{ tooltip: 'moreInfoIcon-tooltip' }}>
                            <div className="moreInfoIcon"></div>
                        </Tooltip>
                    )}
                </div>
                <div className={styles.expender}>
                    <img src={isExpended ? collapseIcon : expandIcon} alt="" />
                </div>
            </div>
            {isExpended && (
                <LogRowExpender
                    logData={logData}
                    key={'logexpender_' + logData.id}
                    rulesNames={rulesNames}
                    groupPolicies={groupPolicies}
                    wallNames={wallNames}
                    user={user}
                    allowDesktopMfa={allowDesktopMfa}
                    countriesList={countriesList}
                    hiddenRiskIndicators={hiddenRiskIndicators}
                    internalServiceAccounts={internalServiceAccounts}
                    ssoVendors={ssoVendors}
                    appDrawerContext={appDrawerContext}
                />
            )}
        </div>
    );
};

export default LogRow;
